import { useEffect, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import { TAccount, TCourse, TSummary } from "../../../../@types";
import { apis, callApi } from "../../../../api";
import { eventNames, logEvent } from "../../../../utils/logEvent";

export type TTeachingService = {
	listeners: (() => void)[];
	loading: boolean;
	summary: TSummary | null;
	course: TCourse | null;
	student: TAccount | null;

	addEventListener: (cb: () => void) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;

	setLoading: (loading: boolean) => void;
	setSummary: (summary: TSummary) => void;
	setCourse: (course: TCourse) => void;
	setStudent: (student: TAccount) => void;

	updateStudentProgress: (
		bookingId: number,
		bookingData: {
			airHours: number;
			simulatorHours: number;
			milesFlown: number;
			flightSummary: string;
		},
		summaryData: {
			airHours: number;
			simulatorHours: number;
			milesFlown: number;
			flightSummary: string;
		},
	) => void;
};

export const TeachingService: TTeachingService = {
	listeners: [],
	loading: false,
	summary: null,
	course: null,
	student: null,

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	clearData() {
		this.loading = false;
		this.summary = null;
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setSummary(summary) {
		this.summary = summary;
	},

	setCourse(course) {
		this.course = course;
	},

	setStudent(student) {
		this.student = student;
	},

	updateStudentProgress(bookingId, bookingData, summaryData) {
		if (!this.summary) {
			return;
		}

		const index = this.summary.bookings.findIndex(
			(booking) => booking.id === bookingId,
		);

		if (index === -1) {
			return;
		}

		this.summary.bookings[index] = {
			...this.summary.bookings[index],
			...bookingData,
		};
		this.summary = { ...this.summary, ...summaryData };
	},
};

export function useTeachingInit() {
	const { courseSlug, studentId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const getData = async () => {
			try {
				TeachingService.setLoading(true);
				TeachingService.emitChanges();

				const response = await callApi({
					method: "GET",
					url: `${apis.SUMMARY_BY_TEACHER_TOKEN}/${courseSlug}/${studentId}`,
				});

				if (response?.summary) {
					TeachingService.setSummary(response.summary);
					const classId = searchParams.get("class");
					if (classId === null && response.summary.bookings.length > 0) {
						setSearchParams(
							(prev) => {
								prev.set("class", response.summary.bookings[0].id.toString());
								return prev;
							},
							{ replace: true },
						);
					}
				}
				if (response?.course) {
					TeachingService.setCourse(response.course);
				}
				if (response?.student) {
					TeachingService.setStudent(response.student);
				}
			} catch (error) {
			} finally {
				TeachingService.setLoading(false);
				TeachingService.emitChanges();
			}
		};

		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courseSlug, studentId]);

	useEffect(() => {
		return () => {
			TeachingService.clearData();
		};
	}, []);
}

export function useTeaching() {
	const [loading, setLoading] = useState(TeachingService.loading);
	const [summary, setSummary] = useState(TeachingService.summary);
	const [course, setCourse] = useState(TeachingService.course);
	const [student, setStudent] = useState(TeachingService.student);
	const [searchParams] = useSearchParams();

	const classId = Number(searchParams.get("class") ?? "0");
	const currentBooking = summary?.bookings.find(
		(booking) => booking.id === classId,
	);

	useEffect(() => {
		const updateData = () => {
			setLoading(TeachingService.loading);
			setSummary(TeachingService.summary);
			setCourse(TeachingService.course);
			setStudent(TeachingService.student);
		};

		TeachingService.addEventListener(updateData);

		return () => {
			TeachingService.removeEventListener(updateData);
		};
	}, []);

	const updateStudentProgress = async (
		bookingId: number,
		body: {
			airHours: number;
			simulatorHours: number;
			milesFlown: number;
			flightSummary: string;
		},
	) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.UPDATE_MY_CLASSES}/${bookingId}`,
				body,
			});

			if (!response) {
				return;
			}

			logEvent(eventNames.updateStudentProgress, {
				bookingId,
			});

			const newBooking = response.booking;
			const newSummary = response.summary;

			TeachingService.updateStudentProgress(
				bookingId,
				{
					airHours: newBooking.airHours,
					simulatorHours: newBooking.simulatorHours,
					milesFlown: newBooking.milesFlown,
					flightSummary: newBooking.flightSummary,
				},
				{
					airHours: newSummary.airHours,
					simulatorHours: newSummary.simulatorHours,
					milesFlown: newSummary.milesFlown,
					flightSummary: newSummary.flightSummary,
				},
			);
			TeachingService.emitChanges();
		} catch (error) {}
	};

	return {
		loading,
		summary,
		course,
		student,
		currentBooking,
		updateStudentProgress,
	};
}
